import { ChartData, Plugin } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { Stage } from "../../shared/CaseConfiguration.ts";
import { StageCosts } from "../../shared/CaseModelUtility.ts";
import { lockedAt } from "../../shared/costChanges.ts";
import { LockedCosts } from "../../shared/CostModel.ts";
import { getTicks, getTooltip, wrapLabel } from "../../utils/chartjsTheming.ts";
import { getColorForCosts } from "../analytics/CostColors.ts";
import { percentage } from "../../shared/percentage.ts";
import { CostCriteria } from "./Budgeting.tsx";
import { useTheme } from "../../context/ThemeContext";

/**
 * Outlines the bars of the datasets at the given indices with a dashed line,
 * which Chart.js does not draw for bars itself.
 */
const dashedBars = (indices: number[]): Plugin<"bar"> => ({
  id: "dashedBars",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    for (const index of indices) {
      const meta = chart.getDatasetMeta(index);
      if (meta.hidden) continue;
      ctx.save();
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = String(chart.data.datasets[index].borderColor);
      for (const bar of meta.data) {
        const { x, y, base, width } = bar.getProps(
          ["x", "y", "base", "width"],
          true,
        ) as { x: number; y: number; base: number; width: number };
        if (Math.abs(base - y) < 1) continue;
        ctx.strokeRect(
          x - width / 2,
          Math.min(y, base),
          width,
          Math.abs(base - y),
        );
      }
      ctx.restore();
    }
  },
});

export const CostsBarChart = ({
  data,
  criteria = "bySpending",
  showYLabels = true,
  onFit,
  locked,
}: {
  data: StageCosts[];
  criteria?: CostCriteria;
  showYLabels?: boolean;
  onFit?: (xAxisStart: number) => void;
  // what the party's settlements locked: shown below the X axis, what of it
  // was incurred and what is not to be spent
  locked?: LockedCosts;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "costBarChart" });
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const dropped = data.map(({ stage }) =>
    lockedAt(locked ?? { ended: {}, costs: [] }, stage),
  );
  const bySpending =
    locked && locked.costs.length > 0
      ? [
          {
            label: t("incurred"),
            data: data.map(({ costs }, index) =>
              costs.getIncurredTotal().minus(dropped[index].incurred).toNumber(),
            ),
            ...getColorForCosts("incurred", theme),
            borderWidth: 2,
          },
          {
            label: t("estimated"),
            data: data.map(({ costs }, index) =>
              costs
                .getEstimatedTotal()
                .minus(dropped[index].estimated)
                .toNumber(),
            ),
            ...getColorForCosts("estimated", theme),
            borderWidth: 2,
          },
          {
            label: t("droppedIncurred"),
            data: dropped.map(({ incurred }) => -incurred.toNumber()),
            ...getColorForCosts("dropped", theme),
            borderWidth: 2,
          },
          {
            label: t("notToBeSpent"),
            data: dropped.map(({ estimated }) => -estimated.toNumber()),
            ...getColorForCosts("notToBeSpent", theme),
            // drawn dashed by the plugin below
            borderWidth: 0,
          },
        ]
      : undefined;

  const chartData: ChartData<"bar", number[]> = {
    labels: data.map((stageCosts) =>
      wrapLabel(t("stages." + Stage[stageCosts.stage]))
    ),
    datasets:
      criteria === "bySpending" && bySpending
        ? bySpending
        : criteria === "bySpending"
        ? [
            {
              label: t("incurred"),
              data: data.map(({ costs }) =>
                costs.getIncurredTotal().toNumber()
              ),
              ...getColorForCosts("incurred", theme),
              borderWidth: 2,
            },
            {
              label: t("estimated"),
              data: data.map(({ costs }) =>
                costs.getEstimatedTotal().toNumber()
              ),
              ...getColorForCosts("estimated", theme),
              borderWidth: 2,
            },
          ]
        : [
            {
              label: t("costTypes.lawFirmFee"),
              data: data.map(({ costs }) =>
                costs.getAll().lawFirmFees.toNumber()
              ),
              ...getColorForCosts("lawFirmFee", theme),
              borderWidth: 2,
            },
            {
              label: t("costTypes.courtFee"),
              data: data.map(({ costs }) =>
                costs.getAll().disbursements.courtFee.toNumber()
              ),
              ...getColorForCosts("courtFee", theme),
              borderWidth: 2,
            },
            {
              label: t("costTypes.barristerFee"),
              data: data.map(({ costs }) =>
                costs.getAll().disbursements.barristerFee.toNumber()
              ),
              ...getColorForCosts("barristerFee", theme),
              borderWidth: 2,
            },
            {
              label: t("costTypes.otherDisbursement"),
              data: data.map(({ costs }) =>
                costs.getAll().disbursements.otherDisbursement.toNumber()
              ),
              ...getColorForCosts("otherDisbursement", theme),
              borderWidth: 2,
            },
          ],
  };

  const dashed = chartData.datasets
    .map(({ label }, index) => (label === t("notToBeSpent") ? index : -1))
    .filter((index) => index >= 0);

  return (
    <Bar
      data={chartData}
      plugins={[dashedBars(dashed)]}
      options={{
        interaction: {
          mode: "index",
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...getTooltip(theme),
            filter: (item) => item.parsed.y !== 0,
            callbacks: {
              // what settlements dropped stands below the axis: as much as
              // it is all the same
              footer: function (items) {
                let total = 0;
                items.forEach((item) => {
                  const dataset = chartData.datasets[item.datasetIndex];
                  const value = dataset.data[item.dataIndex];
                  total += Math.abs(value);
                });
                return t("total", { amount: currency.format(total) });
              },

              label: (item) => {
                const total = chartData.datasets.reduce(
                  (previous, current) =>
                    previous + Math.abs(current.data[item.dataIndex]),
                  0
                );
                const value = Math.abs(item.parsed.y ?? 0);
                return total === 0
                  ? `${item.dataset.label}: ${currency.format(value)}`
                  : `${item.dataset.label}: ${currency.format(value)} (${percentage.format(value / total)})`;
              },
            },
          },
        },
        scales: {
          y: {
            ticks: {
              ...getTicks(theme),
              callback: function (value) {
                if (typeof value === "number")
                  return currency.format(Math.abs(value));
                return value;
              },
              display: showYLabels,
            },
            grid: {
              // make 0 line thicker
              lineWidth: (context) => (context.tick.value == 0 ? 2 : 1),
            },
            stacked: true,
            afterFit: onFit ? ({ right }) => onFit(right) : undefined,
          },
          x: {
            ticks: { ...getTicks(theme), maxRotation: 0, autoSkip: false },
            grid: {
              color: () => undefined,
            },
            stacked: true,
          },
        },
      }}
    />
  );
};
